import { MapPin, Phone, Clock, Instagram, MessageCircle, Mail } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { generalWaLink } from "@/lib/whatsapp";
import { Reveal } from "@/components/Reveal";

export default function Contact({ contact }) {
  const { t, lang } = useLang();
  const c = t.contact;
  const info = contact || {};
  const loc = info[lang] || info.fr || {};
  const phoneDisplay = info.phone_display || "055-500-4933";
  const waLink = generalWaLink(info.phone_intl || "972555004933", lang);

  const rows = [
    { icon: MapPin, label: c.addressLabel, value: loc.address, dir: "ltr" },
    { icon: Phone, label: c.phoneLabel, value: phoneDisplay, dir: "ltr" },
    { icon: Mail, label: c.emailLabel, value: info.email, dir: "ltr", href: info.email ? `mailto:${info.email}` : undefined },
    { icon: Clock, label: c.hoursLabel, value: loc.hours },
    { icon: Instagram, label: c.instagramLabel, value: info.instagram_handle, dir: "ltr" },
  ];

  return (
    <section id="contact" className="bg-ivory py-20 md:py-32 scroll-mt-20" data-testid="contact-section">
      <div className="max-w-6xl mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-14 items-start">
        <Reveal>
          <div>
            <span className="text-xs tracking-[0.22em] uppercase text-gold font-medium">{c.overline}</span>
            <h2 className="mt-4 font-serif text-3xl md:text-5xl text-ink">{c.title}</h2>
            <p className="mt-4 text-taupe">{c.byAppointment}</p>

            <div className="mt-10 space-y-6">
              {rows.map((r, idx) => (
                <div key={idx} className="flex items-start gap-4" data-testid={`contact-row-${idx}`}>
                  <span className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cream text-gold">
                    <r.icon className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-taupe/70">{r.label}</p>
                    {r.href ? (
                      <a href={r.href} className="text-ink text-base md:text-lg hover:text-gold transition-colors" dir={r.dir} data-testid={`contact-value-${idx}`}>
                        {r.value}
                      </a>
                    ) : (
                      <p className="text-ink text-base md:text-lg" dir={r.dir} style={r.dir === "ltr" && lang === "he" ? { unicodeBidi: "plaintext" } : undefined}>
                        {r.value}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <a href={info.maps_url} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center justify-center gap-2 rounded-full bg-gold px-6 py-3.5 text-sm font-medium text-white hover:brightness-105 hover:scale-[1.03] transition-[filter,transform]"
                 data-testid="contact-maps-button">
                <MapPin className="h-4 w-4" /> {c.openMaps}
              </a>
              <a href={waLink} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center justify-center gap-2 rounded-full border border-whatsapp px-6 py-3.5 text-sm font-medium text-[#128C4B] hover:bg-whatsapp hover:text-white transition-colors"
                 data-testid="contact-whatsapp-button">
                <MessageCircle className="h-4 w-4" /> {c.whatsappBtn}
              </a>
            </div>
            <a href={info.instagram_url} target="_blank" rel="noopener noreferrer"
               className="mt-4 inline-flex items-center gap-2 text-sm text-taupe hover:text-gold transition-colors"
               data-testid="contact-instagram-link">
              <Instagram className="h-4 w-4" /> {info.instagram_handle}
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <a href={info.maps_url} target="_blank" rel="noopener noreferrer"
             className="group block relative aspect-[4/3] lg:aspect-[3/4] overflow-hidden rounded-3xl ring-1 ring-gold/25"
             data-testid="contact-map-card">
            <iframe
              title="Netanya"
              className="h-full w-full pointer-events-none"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              src="https://www.google.com/maps?q=Dizengoff+Street+Netanya&output=embed"
            />
            <span className="absolute inset-x-0 bottom-0 bg-ivory/90 backdrop-blur px-5 py-3 text-sm text-ink flex items-center gap-2">
              <MapPin className="h-4 w-4 text-gold" /> {loc.address}
            </span>
          </a>
        </Reveal>
      </div>
    </section>
  );
}
